import { useEffect, useState } from 'react';
import { useRange } from 'react-instantsearch';

import { Box } from '@/components/Box';
import type { RangeSliderProps } from '@/components/RangeSlider';
import { RangeSlider } from '@/components/RangeSlider';
import { useRouter } from '@/hooks/useRouter';

interface TypeDropdownProps extends RangeSliderProps {
  attribute: 'price';
}

const formatPrice = (price: any, defaultValue: number) =>
  isNaN(Number(price)) || price === '' ? defaultValue : Number(price);

export const RangeFilter = ({ attribute, ...props }: TypeDropdownProps) => {
  const { query } = useRouter();
  const { range, refine, start } = useRange({
    attribute,
  });

  // Initialize from start values if they exist (InstantSearch has refinement),
  // otherwise use full range
  const getInitialRange = (): [number, number] => {
    // If InstantSearch already has refinement values, use those
    if (start && start[0] !== -Infinity && start[1] !== Infinity) {
      return [
        formatPrice(start[0], range.min ?? 0),
        formatPrice(start[1], range.max ?? 0),
      ];
    }
    // Otherwise use full range
    return [range.min ?? 0, range.max ?? 0];
  };

  const [selectedRange, setSelectedRange] =
    useState<[number, number]>(getInitialRange);

  // Only refine when user interacts with the slider (not on every selectedRange change)
  const handleAfterChange = (newRange: [number, number]) => {
    setSelectedRange(newRange);
    refine(newRange);
  };

  // Sync from InstantSearch start values when they change
  // This handles the case where InstantSearchStateUpdater sets the state
  useEffect(() => {
    if (start && (start[0] !== -Infinity || start[1] !== Infinity)) {
      const newMin = formatPrice(start[0], range.min ?? 0);
      const newMax = formatPrice(start[1], range.max ?? 0);
      // Only update if actually different to avoid loops
      setSelectedRange((prev) => {
        if (prev[0] !== newMin || prev[1] !== newMax) {
          return [newMin, newMax];
        }
        return prev;
      });
    }
  }, [start, range.min, range.max]);

  // Initialize slider position to full range when Algolia data becomes available
  // But only if there's no active refinement
  useEffect(() => {
    if (
      range.min !== undefined &&
      range.max !== undefined &&
      (!start || (start[0] === -Infinity && start[1] === Infinity))
    ) {
      const min = range.min;
      const max = range.max;
      setSelectedRange((prev) => {
        // Only update if currently at 0,0 (uninitialized)
        if (prev[0] === 0 && prev[1] === 0) {
          return [min, max];
        }
        return prev;
      });
    }
  }, [range.min, range.max, start]);

  // Initialize from URL query params on mount
  useEffect(() => {
    if (!query.priceFrom && !query.priceTo) return;
    const [priceFrom, priceTo] = ['priceFrom', 'priceTo'].map((key) => {
      const value = query[key];
      return Array.isArray(value) ? value[0] : value || '';
    });
    const newRange: [number, number] = [
      formatPrice(priceFrom, range.min ?? 0),
      formatPrice(priceTo, range.max ?? 0),
    ];
    setSelectedRange(newRange);
    refine(newRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return (
    <Box data-test-id='RangeFilterBox' padding={3} style={{ width: '100%' }}>
      <RangeSlider
        max={range.max}
        min={range.min}
        minDistance={10}
        onAfterChange={handleAfterChange}
        pearling
        value={selectedRange}
        {...props}
        data-test-id='RangeFilterRangeSlider'
      />
    </Box>
  );
};

RangeFilter.displayName = 'RangeFilter';
